<template>
	<div :class="getAccordionClassName">
		<div
			class="clickable accordion-header"
			ref="AccordionHeader"
			@click="toggleOne()"
		>
			<p class="accordion-label">{{ label }}</p>

			<div :class="headerClass ? headerClass : 'slot-container'">
				<slot name="accordionHeader"></slot>
			</div>

			<i class="accordion-icon fas fa-chevron-down" />
		</div>

		<transition
			name="accordion-transition"
			@after-enter="afterEnter"
			@after-leave="afterLeave"
		>
			<div
				class="children-container"
				v-if="isExpanded"
			>
				<slot name="accordionBody"></slot>
			</div>
		</transition>
	</div>
</template>
<script lang="ts">
	export interface IAccordionProps {
		label?: string;
		index?: number;
		updateSelectedAccordion?: any;
		disabled?: boolean;
		customDisable?: any;
		headerClass?: string;
		defaultExpanded?: boolean;
		selectedAccordion?: number;
	}
</script>
<script setup lang="ts">
	import { computed, ref, watch } from 'vue';

	const emits = defineEmits(['closed', 'opened']);
	const { label, index, updateSelectedAccordion, disabled, customDisable, headerClass, defaultExpanded, selectedAccordion = null } = defineProps<IAccordionProps>();
	const isExpanded = ref(false);
	const callback = ref(null);

	isExpanded.value = defaultExpanded ? true : false;

	const getAccordionClassName = computed(() => {
		let className = 'Accordion';

		if (isExpanded.value) className += ' expanded';

		return className;
	});

	const isOpen = computed(() => index === selectedAccordion);

	watch(
		() => isOpen.value,
		function (newValue, oldValue) {
			if (newValue !== oldValue) {
				toggle();
			}
		},
	);

	function afterEnter() {
		emits('opened');
		if (callback.value) {
			callback.value();
			callback.value = null;
		}
	}
	function afterLeave() {
		emits('closed');
		if (callback.value) {
			callback.value();
			callback.value = null;
		}
	}
	function toggle(toggleOverride?: boolean) {
		if (!(toggleOverride == null || typeof toggleOverride === 'undefined')) isExpanded.value = toggleOverride;
		else isExpanded.value = !isExpanded.value;
	}
	function toggleOne() {
		if (disabled || customDisable) return;

		if (index != null) {
			updateSelectedAccordion(index);
		} else {
			toggle();
		}
	}
	function toggleOneWithCallback(callback?: any) {
		callback.value = callback;
		toggleOne();
	}

	defineExpose({
		isExpanded,
		toggle,
		toggleOneWithCallback,
	});
</script>

<style>
	.Accordion {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		border: 1px solid var(--border-color);
	}

	.Accordion .accordion-icon {
		transition: var(--transition);
		transform: rotate(0deg);
	}

	.Accordion:has(.children-container.accordion-transition-enter-active) {
		overflow: hidden;
	}

	.Accordion.expanded .accordion-icon {
		transform: rotate(180deg);
	}

	.Accordion .accordion-header {
		width: 100%;
		padding: 0 2%;
		box-sizing: border-box;
		position: relative;
		top: 0;
		z-index: 11;
		display: flex;
		align-items: center;
		background: var(--background-color);
		box-shadow: 0px 2px 10px 0px var(--black-10percent);
		transition: var(--transition);
	}

	.Accordion.expanded .accordion-header {
		position: sticky;
		box-shadow: 1px 3px 15px 1px var(--black-10percent);
		border-top: 1px solid var(--border-color);
		border-bottom: 1px solid var(--border-color);
	}

	.Accordion .accordion-header .slot-container {
		position: absolute;
		right: 5%;
		display: flex;
		gap: 5px;
		padding: 0 5px;
		max-width: 50%;
	}

	.Accordion .accordion-header .accordion-icon {
		position: absolute;
		right: 2%;
	}

	.Accordion .accordion-header.isOpened {
		border-bottom: 1px solid var(--border-color);
	}

	.Accordion .children-container {
		width: 95%;
		margin-top: 10px;
	}

	.accordion-transition-enter-active,
	.accordion-transition-leave-active {
		opacity: 1;
		/*max-height: 200px;*/
		transition: all 0.2s linear;
	}

	.accordion-transition-enter-from,
	.accordion-transition-leave-to {
		max-height: 0;
		opacity: 0;
		overflow: hidden;
	}
</style>
